/**
 * Remote tutors (onboarding records).
 */

import { apiClient } from '@/services/api-client';
import type {
  TutorRecord,
  OnboardingFilters as TutorFilters,
  FullState,
  ProfileDto,
} from '@/types/remote/tutor.types';
import type {
  ArchiveEligibility,
  ArchiveRemoteUserDto,
  ArchiveRemoteUserResult,
  ReactivationEligibility,
  ReactivateRemoteUserResult,
  WithPhoneSetupLink,
} from '@/types/user.types';

/** `POST /tutors-remote` — create tutor + onboarding record. */
export const createProfile = async (
  dto: ProfileDto,
  companyId: string,
): Promise<{ user: unknown; record: unknown }> => {
  const response = await apiClient.post<{ user: unknown; record: unknown }>(
    `/tutors-remote?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** `PATCH /tutors-remote/:userId` */
export const updateProfile = async (
  userId: string,
  dto: ProfileDto,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> => {
  const response = await apiClient.patch<WithPhoneSetupLink<unknown>>(
    `/tutors-remote/${userId}?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** Flatten filters; skip `sansRi`/`sansCj`/`activable` unless `true`. */
function toListParams(
  filters: Omit<TutorFilters, 'companyId'> & { companyId?: string },
  companyId?: string,
): URLSearchParams {
  const params = companyId ? new URLSearchParams({ companyId }) : new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'companyId' || value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
      continue;
    }
    if ((key === 'sansRi' || key === 'sansCj' || key === 'activable') && value !== true) continue;
    if (key === 'programId' && filters.programIds?.length) continue;
    params.append(key, String(value));
  }
  return params;
}

/** `GET /tutors-remote` — scoped list (no companyId required in the path). */
export async function listScoped(
  filters: Omit<TutorFilters, 'companyId'>,
): Promise<{ data: TutorRecord[]; total: number }> {
  const params = toListParams(filters);
  const { data } = await apiClient.get<{ data: TutorRecord[]; total: number }>(
    `/tutors-remote?${params}`,
  );
  return data;
}

/** `GET /onboarding/tutor-remote/:userId` — record + steps + templates. */
export async function getFullState(userId: string, companyId: string): Promise<FullState> {
  const { data } = await apiClient.get<FullState>(
    `/onboarding/tutor-remote/${userId}?companyId=${companyId}`,
  );
  return data;
}

/** `POST /onboarding/tutor-remote/:userId/information-session/participate` */
export async function markInformationSessionParticipation(
  userId: string,
  companyId: string,
  sessionId?: string,
  participated: boolean = true,
): Promise<TutorRecord> {
  const { data } = await apiClient.post<TutorRecord>(
    `/onboarding/tutor-remote/${userId}/information-session/participate?companyId=${companyId}`,
    { sessionId, participated },
  );
  return data;
}

/** `PATCH /tutors-remote/:userId` with `sessionId` to reserve an RI. */
export async function reserveInformationSession(
  userId: string,
  companyId: string,
  sessionId: string,
): Promise<TutorRecord> {
  const { data } = await apiClient.patch<TutorRecord>(
    `/tutors-remote/${userId}?companyId=${companyId}`,
    { sessionId },
  );
  return data;
}

/** `PATCH /tutors-remote/:userId` with `sessionId: null` after canceling an RI booking. */
export async function clearInformationSessionReservation(
  userId: string,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> {
  return updateProfile(userId, { sessionId: null }, companyId);
}

/** `GET /onboarding/tutor-remote/export` — Excel blob. */
export async function exportExcel(
  filters: Omit<TutorFilters, 'companyId' | 'page' | 'pageSize'>,
  companyId: string,
): Promise<Blob> {
  const params = toListParams(filters, companyId);
  const { data } = await apiClient.get<Blob>(`/onboarding/tutor-remote/export?${params}`, {
    responseType: 'blob',
  });
  return data;
}

/** `GET /tutors-remote/:userId/archive-eligibility` */
export async function getArchiveEligibility(
  userId: string,
  companyId: string,
): Promise<ArchiveEligibility> {
  const { data } = await apiClient.get<ArchiveEligibility>(
    `/tutors-remote/${userId}/archive-eligibility?companyId=${companyId}`,
  );
  return data;
}

/** `POST /tutors-remote/:userId/archive` */
export async function archiveUser(
  userId: string,
  companyId: string,
  dto: ArchiveRemoteUserDto,
): Promise<ArchiveRemoteUserResult> {
  const { data } = await apiClient.post<ArchiveRemoteUserResult>(
    `/tutors-remote/${userId}/archive?companyId=${companyId}`,
    dto,
  );
  return data;
}

/** `GET /tutors-remote/:userId/reactivation-eligibility` */
export async function getReactivationEligibility(
  userId: string,
  companyId: string,
): Promise<ReactivationEligibility> {
  const { data } = await apiClient.get<ReactivationEligibility>(
    `/tutors-remote/${userId}/reactivation-eligibility?companyId=${companyId}`,
  );
  return data;
}

/** `POST /tutors-remote/:userId/reactivate` */
export async function reactivateUser(
  userId: string,
  companyId: string,
): Promise<ReactivateRemoteUserResult> {
  const { data } = await apiClient.post<ReactivateRemoteUserResult>(
    `/tutors-remote/${userId}/reactivate?companyId=${companyId}`,
    {},
  );
  return data;
}
